import { Permissions } from '../../../core/constants/permissions';
import { AuthService } from '../../../core/services/auth.service';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DropdownInputComponent } from '../../../shared/dropdown-input/dropdown-input.component';
import { TrialDetail } from '../../../core/models/trial-detail.model';
import { TrialService } from '../../../core/services/trial.service';
import { TranslationService } from '../../../core/services/translation.service';
import { LogHelper } from '../../../core/helpers/log.helper';
import { AlertService } from '../../../shared/alert/alert.service';
import { TemplateService } from '../../../core/services/template.service';
import { ModalComponent } from '../../../shared/modal/modal.component';
import { TrialConstants, TrialStateString } from '../../../core/constants/trial-constants';
import { Countries } from '../../../core/constants/countries';
import { TrialUpdate } from '../../../core/services/interfaces/trial-update.interface';
import { Cultures } from '../../../core/constants/cultures';
import { GlobalConstants } from '../../../core/constants/global-constants';
import { Currencies } from '../../../core/constants/currency';
import { AutosuggestDropdownInputComponent } from '../../../shared/autosuggest-dropdown-input/autosuggest-dropdown-input.component';
import { ApiConsumerService } from '../../../core/services/api-consumer.service';
import { SelectOption } from '../../../core/models/select-option.model';
import { ApiConsumerSimple } from '../../../core/models/api-consumer-simple.model';
import { ListAutocompleteComponent } from '../../../shared/list-autocomplete/list-autocomplete.component';
import { BrandService } from '../../../core/services/brand.service';
import { Brand } from '../../../core/models/brand.model';
import { noWhitespaceValidator, requiredNoWhitespaceValidator } from '../../../core/validators/no-whitespace.validator';
import { FeatureService } from '../../../core/services/feature.service';

@Component({
  selector: 'app-trial-edit',
  templateUrl: './trial-edit-v2.component.html',
  styleUrls: ['./trial-edit.component.scss']
})
export class TrialEditComponent implements OnInit {
  @ViewChild('languageSelect') languageSelect: DropdownInputComponent;
  @ViewChild('translationVersionDropdown') translationVersionDropdown: DropdownInputComponent;
  @ViewChild('categoriesSelect') categoriesSelect: ElementRef;
  @ViewChild('visitCountElement') visitCountElement: DropdownInputComponent;
  @ViewChild('stateElement') stateElement: DropdownInputComponent;
  @ViewChild('addLanguageModal') addLanguageModal: ModalComponent;
  @ViewChild('removeLanguageModal') removeLanguageModal: ModalComponent;
  @ViewChild('brandChangeModal') brandChangeModal: ModalComponent;
  @ViewChild('apiDisableModal') apiDisableModal: ModalComponent;
  @ViewChild('brandSelect') brandSelect: DropdownInputComponent;
  @ViewChild('therapeuticAreaSelect') therapeuticAreaSelect: DropdownInputComponent;
  @ViewChild('baseCurrencySelect') baseCurrencySelect: AutosuggestDropdownInputComponent;
  @ViewChild('imsSupplierList') imsSupplierList: ListAutocompleteComponent;

  emailTemplateOptions: { id: string, value: string}[] = [{ id: '0', value: "PatientGo" },{ id: '1', value: "StudyKik" }];
  trial = new TrialDetail();
  expenseCategories: { id: string, value: string }[] = [];
  currencyOptions: { value: string; text: string }[] = [];
  defaultForm: UntypedFormGroup;
  forms: UntypedFormGroup[] = [];
  languageSelectForm: UntypedFormGroup;
  visitCountOptions = Array<{ value: string, text: string }>();
  versionOptions: { value: string, text: string }[] = [];
  states: { value: string, text: string }[] = [];
  cultureSelectedValue: string = '';
  trialDashboardEnabled = TrialConstants.trialDashboardEnabled;
  therapeuticAreaOptions = TrialConstants.therapeuticAreaOptions;

  allCultures: { value: string, text: string }[] = [];
  culturesInUse: { value: string, text: string }[] = [];
  currentCulture = 'en';
  countries = Countries.all();
  selectedCountries: { code: string, name: string }[] = [];
  Permissions = Permissions;
  apiConsumerOptions: SelectOption[] = [];
  // Ids of the third-party consumers, so the selection rules do not need to re-query the service. AB#34841.
  thirdPartyConsumerIds = new Set<string>();
  brandName: string = '';
  brands: Brand[] = [];
  brandOptions: SelectOption[] = [];
  currentBrand: Brand;
  brandChangeConfirmed = false;
  // AB#34863. Switching API integration off clears the consumer set and withdraws API access, so it is
  // confirmed once per save attempt rather than applied silently.
  apiDisableConfirmed = false;

  isRequired(controlName: string): boolean {
    const control = this.defaultForm?.get(controlName);
    if (!control || !control.validator) return false;
    const errors = control.validator({value: ''} as any);
    return errors != null && (errors['required'] === true || errors['whitespace'] === true);
  }

  constructor(
    private brandService: BrandService,
    private readonly trialService: TrialService,
    private readonly alertService: AlertService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly templateService: TemplateService,
    private readonly translationService: TranslationService,
    protected readonly featureService: FeatureService,
    public authService: AuthService,
    private readonly apiConsumerService: ApiConsumerService,
    private cdr: ChangeDetectorRef) {
    for (const culture of Cultures.all()) {
      this.allCultures.push({ value: culture.culture, text: culture.name });
    }
  }

  ngOnInit() {
    this.templateService.showHeader();

    // Create options 1..50 for the visit count dropdown
    for (let i = 1; i <= 50; i++) {
      this.visitCountOptions.push({ value: i.toString(), text: i.toString() });
    }

    // Populate Currencies drop down
    for (const currency of Currencies.all()) {
      this.currencyOptions.push({ value: currency.cc, text: currency.cc });
    }

    // Build the add new trial form
    this.defaultForm = new UntypedFormGroup({
      isBusy: new FormControl(false),
      id: new UntypedFormControl(''),
      culture: new UntypedFormControl('en'),
      state: new UntypedFormControl(this.trial.state, Validators.required),
      code: new UntypedFormControl(this.trial.code, [requiredNoWhitespaceValidator(), Validators.maxLength(50)]),
      internalCode: new UntypedFormControl(this.trial.internalCode, [requiredNoWhitespaceValidator(), Validators.maxLength(50)]),
      name: new UntypedFormControl({ value: '', disabled: true }),
      client: new UntypedFormControl({ value: '', disabled: true }, [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
      sponsor: new UntypedFormControl('', noWhitespaceValidator()),
      nickname: new UntypedFormControl('', [noWhitespaceValidator(), Validators.maxLength(200)]),
      opportunityNumber: new UntypedFormControl(requiredNoWhitespaceValidator()),
      protocolNumber: new UntypedFormControl('', [Validators.maxLength(200)]),
      indications: new UntypedFormControl('', [noWhitespaceValidator(), Validators.maxLength(1024)]),
      therapeuticArea: new UntypedFormControl(''),
      visitCount: new UntypedFormControl(this.trial.visitCount),
      description: new UntypedFormControl('', [noWhitespaceValidator(), Validators.maxLength(500)]),
      expensePolicy: new UntypedFormControl('', [Validators.maxLength(5000)]),
      expenseCategories: new UntypedFormControl([]),
      allowTravelRequests: new UntypedFormControl(this.trial.allowTravelRequests),
      allowExpenseRequests: new UntypedFormControl(this.trial.allowExpenseRequests),
      allowActivityCentre: new UntypedFormControl(this.trial.allowActivityCentre),
      enableSmartNotifications: new UntypedFormControl(this.trial.enableSmartNotifications),
      translationVersionId: new UntypedFormControl(this.trial.translationVersionId, Validators.required),
      rideHealthEnabled: new UntypedFormControl(false),
      dashboardEnabled: new UntypedFormControl(false),
      allowFeedbackRequests: new UntypedFormControl(false),
      termsUrl: new UntypedFormControl(this.trial.termsUrl, [requiredNoWhitespaceValidator(), Validators.pattern(GlobalConstants.urlValidatorPattern)]),
      privacyPolicyUrl: new UntypedFormControl(this.trial.privacyPolicyUrl, [requiredNoWhitespaceValidator(), Validators.pattern(GlobalConstants.urlValidatorPattern)]),
      studyMailbox: new UntypedFormControl(this.trial.studyMailbox, [requiredNoWhitespaceValidator(), Validators.email]),
      coordinatorEmail: new UntypedFormControl(this.trial.coordinatorEmail, Validators.email),
      coordinatorName: new UntypedFormControl(this.trial.coordinatorName, noWhitespaceValidator()),
      pmName: new UntypedFormControl(this.trial.pmName, noWhitespaceValidator()),
      pmEmail: new UntypedFormControl(this.trial.pmEmail, Validators.email),
      countries: new UntypedFormControl([]),
      expensePolicyReminders: new UntypedFormControl('', noWhitespaceValidator()),
      bookingEnabled: new UntypedFormControl(false),
      patientAutoCompletion: new UntypedFormControl(false),
      bankAccountEnabled: new UntypedFormControl(false),
      bankAccountSelfManagementEnabled: new UntypedFormControl(false),
      baseCurrency: new UntypedFormControl('', requiredNoWhitespaceValidator()),
      bankAccountSelfCardManagementEnabled: new UntypedFormControl(false),
      virtualCardInAppEnabled: new UntypedFormControl(false),
      nmibleBankInAppEnabled: new UntypedFormControl(false),
      muvEnabled: new UntypedFormControl(false),
      apiEnabled: new UntypedFormControl(false),
      // AB#34729: the patchValue below already supplied apiConsumerIds; with no control declared for it
      // Angular silently discarded the value, which is why the field could only ever be read-only.
      apiConsumerIds: new UntypedFormControl([]),
      brandId: new UntypedFormControl('', Validators.required),
      requestTravelEmailTemplate: new UntypedFormControl('1'),
      magicLinkExpiryDays: new UntypedFormControl(1),
      emailPatientOnVisitCreation: new UntypedFormControl(false),
      diaryEnabled: new UntypedFormControl(false),
      genericDiaryEnabled: new UntypedFormControl(false),
      imsSupplier: new UntypedFormControl('', Validators.maxLength(200)),
      imsLegacyTrial: new UntypedFormControl(false),
      sharepointLink: new UntypedFormControl('', noWhitespaceValidator()),
      travelAdditionalInfoEnabled: new UntypedFormControl(false),
      testDataOnly: new UntypedFormControl(this.trial.testDataOnly),
      faqsEnabled: new UntypedFormControl(this.trial.faqsEnabled),
      selfServeNotificationsEnabled: new UntypedFormControl(false),
      receiptLeadExpenseEnabled: new UntypedFormControl(false),
      dailySpendExpensesEnabled: new UntypedFormControl(false),
      dailySpendExpenseAppEnabled: new UntypedFormControl(false),
      virtualCallsEnabled: new UntypedFormControl(false),
      recordingAskForConsent: new UntypedFormControl(false),
    });

    this.loadAndSetApiConsumerOptions();

    if (this.authService.hasPermission(Permissions.TrialEditSensitive)) {
      this.defaultForm.get("protocolNumber").addValidators(Validators.required);
      this.defaultForm.get("client").addValidators(Validators.required);
      this.defaultForm.get("name").addValidators(Validators.required);
    } else {
      // Disable fields hidden behind TrialEditSensitive so they are excluded from validation
      this.defaultForm.get("name").disable();
      this.defaultForm.get("client").disable();
      this.defaultForm.get("sponsor").disable();
      this.defaultForm.get("protocolNumber").disable();
      this.defaultForm.get("indications").disable();
      this.defaultForm.get("description").disable();
      this.defaultForm.get("therapeuticArea").disable();
    }

    if (!this.authService.hasPermission(Permissions.TrialEditPublic)) {
      // Disable field hidden behind TrialEditPublic so it is excluded from validation
      this.defaultForm.get("code").disable();
    }

    this.languageSelectForm = new UntypedFormGroup({
      culture: new UntypedFormControl('en')
    });

    this.languageSelectForm.get('culture').valueChanges.subscribe(value => {
      this.currentCulture = value;
    });

    // Load translation version
    this.loadTranslationVersion().then(() => {
      // After the translation versions have been loaded, continue to load the trial etc
      this.activatedRoute.params.subscribe(params => {
        this.trialService.retrieveTrial(params.id).subscribe({
          next: trial => {
            this.trial = trial;
            this.populateTrialStates(this.trial.state);
            this.populateFormWithTrial(trial);
          },
          error: error => {
            LogHelper.log(error);
            this.alertService.showWarningAlert('Unable to load trial!');
          }
        });
      });

      this.listenToTrialNameValues();
    });

    // Update country tags
    this.defaultForm.get('countries').valueChanges.subscribe(c => {
      this.selectedCountries = [];
      c.forEach(country => {
        this.selectedCountries.push(this.countries.find(c => c.code === country));
      });
    });

    // When daily spend expenses is disabled, also disable daily spend expense app
    this.defaultForm.get('dailySpendExpensesEnabled').valueChanges.subscribe(enabled => {
      if (!enabled) {
        this.defaultForm.patchValue({dailySpendExpenseAppEnabled: false}, { emitEvent: false });
      }
    });

    // When daily spend expense app is enabled, also enable daily spend expenses
    this.defaultForm.get('dailySpendExpenseAppEnabled').valueChanges.subscribe(enabled => {
      if (enabled) {
        this.defaultForm.patchValue({dailySpendExpensesEnabled: true}, { emitEvent: false });
      }
    });

    // Disable dashboard toggle if e-diary is enabled
    this.defaultForm.valueChanges.subscribe(changes => {
      if (this.defaultForm.value.diaryEnabled || this.defaultForm.value.genericDiaryEnabled) {
        this.defaultForm.get('dashboardEnabled').disable({ emitEvent: false });
        this.defaultForm.patchValue({dashboardEnabled: true}, { emitEvent: false });
      } else {
        this.defaultForm.get('dashboardEnabled').enable({ emitEvent: false });
      }
    });

    this.cdr.detectChanges();
  }

  /**
   * Retrieves and sets the api consumer options to be used in the api consumer dropdown
   */
  loadAndSetApiConsumerOptions(): void {
    this.apiConsumerService.getAllApiConsumers().subscribe({
      next: (apiConsumers: any) => {
        this.thirdPartyConsumerIds = new Set<string>(
          apiConsumers.filter((c: ApiConsumerSimple) => !c.firstPartyConsumer).map((c: ApiConsumerSimple) => c.id));

        this.apiConsumerOptions = apiConsumers.map((apiConsumer: ApiConsumerSimple) => {
          // Which party a consumer belongs to decides what may be combined with it, so it has to be on
          // the face of the option - the name alone gives the operator nothing to reason with. AB#34841.
          const qualifiers = [apiConsumer.firstPartyConsumer ? 'first-party' : 'third-party'];
          if (!apiConsumer.enabled) {
            qualifiers.push('temporarily disabled');
          }
          return new SelectOption(apiConsumer.id, `${apiConsumer.name} (${qualifiers.join(', ')})`);
        });

        // Re-evaluated on every change rather than once, because what is selectable depends entirely on
        // what is already selected. patchValue of the loaded trial emits through here too, so the initial
        // state is covered whichever of the two async loads finishes last.
        this.defaultForm.get('apiConsumerIds').valueChanges
          .subscribe(() => this.applyApiConsumerSelectionRules());
        this.defaultForm.get('apiEnabled').valueChanges.subscribe(enabled => {
          // Un-ticking hides the picker but would otherwise leave the selection in the form, and the API
          // stores a consumer set on a trial with integration off quite happily (ValidateApiConsumerSet is
          // not gated on ApiEnabled, AB#34728). Leaving it behind would silently keep consumers attached to
          // a non-API trial - invisible on the screen, and the exact state that has to be caught the next
          // time someone switches integration on. Clearing here also mirrors what the server does on a
          // switch-off, so the form and the saved result agree. AB#34863 - applies in both directions now
          // that the checkbox is editable on an established API trial too.
          if (!enabled) {
            this.defaultForm.get('apiConsumerIds').setValue([], { emitEvent: false });
          }

          this.applyApiConsumerSelectionRules();
        });
        this.applyApiConsumerSelectionRules();
      }
    });
  }

  /**
   * Greys out the consumers that TrialService.ValidateApiConsumerSet would reject, so an invalid set
   * cannot be built in the first place. AB#34841 - the rules were enforced only on save, which meant the
   * operator discovered them by having the save bounce.
   *
   * A trial uses either one or more first-party consumers, or a single third-party consumer, never a
   * mixture. So one third-party selection excludes everything else, and any first-party selection
   * excludes every third-party consumer.
   *
   * This is an affordance, NOT the enforcement - the server still validates, and must, because the API
   * is reachable without this screen.
   *
   * DELIBERATELY NOT ENFORCED HERE (AB#34863 removed it): whether a third-party consumer may be chosen.
   * AB#34840 greyed third-party out whenever integration was being switched on, which was wrong - a
   * third-party consumer on an EMPTY trial strands nobody and is a legitimate setup. Whether it is allowed
   * depends on the trial having patients or sites, which this screen does not know, so the rule stays
   * server-side where the answer is available. The refusal is readable on the form thanks to AB#34842.
   */
  private applyApiConsumerSelectionRules(): void {
    const selected: string[] = this.defaultForm.get('apiConsumerIds').value ?? [];
    const hasThirdParty = selected.some(id => this.thirdPartyConsumerIds.has(id));
    const hasFirstParty = selected.some(id => !this.thirdPartyConsumerIds.has(id));

    this.apiConsumerOptions = this.apiConsumerOptions.map(option => {
      const next = new SelectOption(option.value, option.text);
      // Never disable something already selected - a disabled mat-option cannot be clicked, so doing so
      // would trap the operator with a set they can no longer undo.
      next.disabled = !selected.includes(option.value)
        && (hasThirdParty || (hasFirstParty && this.thirdPartyConsumerIds.has(option.value)));
      return next;
    });
  }

  /**
   * SUPERSEDED by AB#34729, and now unreferenced. The consumer set is editable on this screen through
   * the apiConsumerIds control, so the trial-edit template no longer renders this summary. Kept rather
   * than deleted: apiEnabled itself is still create-time only (TrialService.Update does not read it),
   * so a read-only rendering of the API setting may well be wanted again, and this is the mapping from
   * consumer ids to display names it would need.
   */
  get apiConsumerNames(): string {
    if (!this.trial?.apiConsumerIds?.length) {
      return '';
    }

    return this.trial.apiConsumerIds
      .map(id => this.apiConsumerOptions.find(option => option.value === id)?.text ?? id)
      .join(', ');
  }

  populateTrialStates(state: string) {
    if (state === TrialStateString.Pending) {
      this.states.push({ value: '1', text: 'Pending' });
      this.states.push({ value: '0', text: 'Live' });
      this.states.push({ value: '3', text: 'Cancelled' });
    }

    if (state === TrialStateString.Live) {
      this.states.push({ value: '0', text: 'Live' });
      this.states.push({ value: '2', text: 'Closed' });
    }

    if (state === TrialStateString.Closed) {
      this.states.push({ value: '2', text: 'Closed' });
      this.stateElement.disabled = true;
    }

    if (state === TrialStateString.Cancelled) {
      this.states.push({ value: '3', text: 'Cancelled' });
      this.stateElement.disabled = true;
    }
  }

  formsValid(): boolean {
    return this.defaultForm.valid && this.forms.every((group) => group.valid);
  }

  getStateValue(state: string) {
    for (let s of this.states) {
      if (s.text === state) {
        return s.value;
      }
    }

    return '';
  }

  isCultureInUse(cultureCode: string): boolean {
    let inUse = false;
    this.culturesInUse.forEach(culture => {
      if (culture.value.toLowerCase() === cultureCode.toLowerCase()) {
        inUse = true;
      }
    });

    return inUse;
  }

  loadTranslationVersion() {
    return new Promise((resolve) => {
      this.versionOptions = [];
      this.translationService.list().subscribe({
        next: (versions: any) => {
          versions.forEach(version => {
            this.versionOptions.push({ value: version.id, text: version.version });
          });

          return resolve(null);
        }
      });
    });
  }

  trialHasCategory(id: string): boolean {
    this.expenseCategories.forEach(category => {
      if (category.id === id)
        return true;
    });

    return false;
  }

  populateFormWithTrial(trial: TrialDetail) {
    // Populate categories
    const trialCategories: string[] = trial.categories.map(category => category.id);
    this.defaultForm.patchValue({
      state: this.getStateValue(trial.state),
      code: trial.code,
      internalCode: trial.internalCode,
      name: trial.name,
      description: trial.description,
      client: trial.client,
      sponsor: trial.sponsor ? trial.sponsor : '',
      nickname: trial.nickname ? trial.nickname : '',
      opportunityNumber: trial.opportunityNumber,
      protocolNumber: trial.protocolNumber,
      indications: trial.indications,
      therapeuticArea: trial.therapeuticArea,
      visitCount: trial.visitCount,
      expenseCategories: trialCategories,
      allowTravelRequests: trial.allowTravelRequests,
      allowExpenseRequests: trial.allowExpenseRequests,
      allowActivityCentre: trial.allowActivityCentre,
      enableSmartNotifications: trial.enableSmartNotifications,
      supportEmail: trial.supportEmail,
      supportPhone: trial.supportPhone,
      termsUrl: trial.termsUrl,
      privacyPolicyUrl: trial.privacyPolicyUrl,
      studyMailbox: trial.studyMailbox,
      coordinatorEmail: trial.coordinatorEmail,
      coordinatorName: trial.coordinatorName,
      pmName: trial.pmName,
      pmEmail: trial.pmEmail,
      translationVersionId: trial.translationVersionId,
      brandId: trial.brandId,
      rideHealthEnabled: trial.rideHealthEnabled,
      dashboardEnabled: trial.dashboardEnabled,
      allowFeedbackRequests: trial.allowFeedbackRequests,
      countries: trial.countries,
      expensePolicyReminders: trial.expensePolicyReminders,
      bookingEnabled: trial.bookingEnabled,
      patientAutoCompletion: trial.patientAutoCompletion,
      bankAccountEnabled: trial.bankAccountEnabled,
      bankAccountSelfManagementEnabled: trial.bankAccountSelfManagementEnabled,
      baseCurrency: trial.baseCurrency,
      bankAccountSelfCardManagementEnabled: trial.bankAccountSelfCardManagementEnabled,
      virtualCardInAppEnabled: trial.virtualCardInAppEnabled,
      nmibleBankInAppEnabled: trial.nmibleBankInAppEnabled,
      muvEnabled: trial.muvEnabled,
      apiEnabled: trial.apiEnabled,
      apiConsumerIds: trial.apiConsumerIds,
      requestTravelEmailTemplate: trial.requestTravelEmailTemplate !== null ? trial.requestTravelEmailTemplate.toString() : '0',
      magicLinkExpiryDays: trial.magicLinkExpiryDays,
      emailPatientOnVisitCreation: trial.emailPatientOnVisitCreation,
      diaryEnabled: trial.diaryEnabled,
      genericDiaryEnabled: trial.genericDiaryEnabled,
      imsSupplier: trial.imsSupplier,
      imsLegacyTrial: trial.imsLegacyTrial,
      sharepointLink: trial.sharepointLink,
      travelAdditionalInfoEnabled: trial.travelAdditionalInfoEnabled,
      testDataOnly: trial.testDataOnly,
      faqsEnabled: trial.faqsEnabled,
      selfServeNotificationsEnabled: trial.selfServeNotificationsEnabled,
      receiptLeadExpenseEnabled: trial.receiptLeadExpenseEnabled,
      dailySpendExpensesEnabled: trial.dailySpendExpensesEnabled,
      dailySpendExpenseAppEnabled: trial.dailySpendExpenseAppEnabled,
      virtualCallsEnabled: trial.virtualCallsEnabled,
      recordingAskForConsent: trial.recordingAskForConsent
    });

    if (trial.baseCurrency !== "") {
      this.baseCurrencySelect.disable();
    }

    if (this.authService.hasPermission(Permissions.TrialEditSensitive)) {
      if (trial.therapeuticArea) {
        this.therapeuticAreaSelect.setValue(trial.therapeuticArea.toString())
      } else {
        this.therapeuticAreaSelect.reset()
      }

      if (trial.baseCurrency) {
        this.baseCurrencySelect.setSelectedOption(trial.baseCurrency);
      } else {
        this.baseCurrencySelect.onClear();
      }

      this.defaultForm.get('client').enable();
    }

    // State
    this.stateElement.setValue(this.getStateValue(trial.state.toString()));

    // Visit count
    this.visitCountElement.setValue(trial.visitCount.toString());

    // Translation version
    //this.translationVersionDropdown.setValue(trial.translationVersionId);

    // Set brand name and options
    this.brandService.getBrands().subscribe({
      next: brands => {
        this.brands = brands;
        this.brandOptions = brands.map(b => new SelectOption(b.id, b.name));
        this.currentBrand = brands.find(b => b.id === this.trial.brandId);
        // Deliberately no imperative `brandSelect.setValue()` here. `brandOptions` only reaches the
        // dropdown's `options` input on the next change-detection cycle, so a `setValue` on this line
        // searches the previous - empty - array, matches nothing, and silently leaves the field showing
        // its placeholder for the life of the screen. That was the reported defect: the saved brand was
        // never displayed on re-opening the trial. The template binds `[selectedValue]` to the form
        // control instead, and the dropdown recomputes its text whenever either input settles, so the
        // order the trial and the brand list arrive in no longer matters. AB#34543.
        //
        // Trial Add has the same shape and works only because it defers its `setValue` behind a 500ms
        // `setTimeout` ("Delay to allow page time to load"). That is a race, not a fix, and is not
        // copied here.
      },
      error: err => {
        this.alertService.showErrorAlert(err);
      }
    });
  }

  onStateChanged(state: string) {
    this.defaultForm.patchValue({ 'state': state });
  }

  /**
   * Called when the add trial form is submitted
   */
  onFormSubmit() {
    if (this.defaultForm.valid) {
      const brandChanged = this.defaultForm.get('brandId').value !== this.trial.brandId;
      if (brandChanged && !this.brandChangeConfirmed) {
        this.brandChangeModal.show();
        return;
      }
      // Only the on -> off direction is confirmed. Switching integration ON is additive and reversible;
      // switching it OFF clears the consumer set and withdraws API access from any connected system, and
      // nothing else on this form says so. AB#34863.
      const disablingApi = this.trial.apiEnabled && !this.defaultForm.get('apiEnabled').value;
      if (disablingApi && !this.apiDisableConfirmed) {
        this.apiDisableModal.show();
        return;
      }

      // BOTH confirmations are cleared here, together, and only once every gate above has passed.
      // Clearing one immediately after its own check - which is what this did while the brand change was
      // the only gate - breaks as soon as a second gate returns early: confirming the brand would reset
      // its flag, the API modal would return, and confirming that re-entered the method with the brand
      // flag already false, so the brand modal reappeared and the two bounced off each other forever.
      // Changing the brand and switching integration off in the same save is an ordinary thing to do.
      // AB#34863.
      this.brandChangeConfirmed = false;
      this.apiDisableConfirmed = false;

      this.defaultForm.patchValue({isBusy: true});

      const mazeDiaryEnabled = this.defaultForm.get('diaryEnabled').value;
      const genericDiaryEnabled = this.defaultForm.get('genericDiaryEnabled').value;

      if (mazeDiaryEnabled && genericDiaryEnabled) {
        this.alertService.showWarningAlert('Maze E-Diary and E-Diary cannot be both enabled on a trial, please disable one.');
        this.defaultForm.patchValue({isBusy: false});
        return;
      }

      const updateTrial: TrialUpdate = {
        trialState: +this.defaultForm.get('state').value,
        trialCode: this.defaultForm.get('code').value,
        internalCode: this.defaultForm.get('internalCode').value,
        name: this.defaultForm.get('name').value,
        description: this.defaultForm.get('description').value,
        client: this.defaultForm.get('client').value,
        sponsor: this.defaultForm.get('sponsor').value,
        nickname: this.defaultForm.get('nickname').value,
        opportunityNumber: this.defaultForm.get('opportunityNumber').value,
        protocolNumber: this.defaultForm.get('protocolNumber').value,
        indications: this.defaultForm.get('indications').value,
        therapeuticArea: +this.defaultForm.get('therapeuticArea').value,
        brandId: this.defaultForm.get('brandId').value,
        baseCurrency: this.defaultForm.get('baseCurrency').value,
        visitCount: +this.visitCountElement.selectedValue,
        allowTravelRequests: this.defaultForm.get('allowTravelRequests').value,
        allowExpenseRequests: this.defaultForm.get('allowExpenseRequests').value,
        allowActivityCentre: this.defaultForm.get('allowActivityCentre').value,
        enableSmartNotifications: this.defaultForm.get('enableSmartNotifications').value,
        expenseCategories: this.defaultForm.get('expenseCategories').value,
        termsUrl: this.defaultForm.get('termsUrl').value,
        privacyPolicyUrl: this.defaultForm.get('privacyPolicyUrl').value,
        studyMailbox: this.defaultForm.get('studyMailbox').value,
        coordinatorEmail: this.defaultForm.get('coordinatorEmail').value,
        coordinatorName: this.defaultForm.get('coordinatorName').value,
        pmName: this.defaultForm.get('pmName').value,
        pmEmail: this.defaultForm.get('pmEmail').value,
        translationVersionId: this.versionOptions[0].value,
        rideHealthEnabled: this.defaultForm.get('rideHealthEnabled').value,
        dashboardEnabled: this.defaultForm.get('dashboardEnabled').value,
        allowFeedbackRequests: this.defaultForm.get('allowFeedbackRequests').value,
        countries: this.defaultForm.get('countries').value,
        expensePolicyReminders: this.defaultForm.get('expensePolicyReminders').value,
        bookingEnabled: this.defaultForm.get('bookingEnabled').value,
        patientAutoCompletion: this.defaultForm.get('patientAutoCompletion').value,
        bankAccountEnabled: this.defaultForm.get('bankAccountEnabled').value,
        bankAccountSelfManagementEnabled: this.defaultForm.get('bankAccountSelfManagementEnabled').value,
        bankAccountSelfCardManagementEnabled: this.defaultForm.get('bankAccountSelfCardManagementEnabled').value,
        virtualCardInAppEnabled: this.defaultForm.get('virtualCardInAppEnabled').value,
        nmibleBankInAppEnabled: this.defaultForm.get('nmibleBankInAppEnabled').value,
        muvEnabled: this.defaultForm.get('muvEnabled').value,
        apiConsumerIds: this.defaultForm.get('apiConsumerIds').value ?? [],
        apiEnabled: this.defaultForm.get('apiEnabled').value,
        requestTravelEmailTemplate: +this.defaultForm.get('requestTravelEmailTemplate').value,
        magicLinkExpiryDays: +this.defaultForm.get('magicLinkExpiryDays').value,
        emailPatientOnVisitCreation: this.defaultForm.get('emailPatientOnVisitCreation').value,
        diaryEnabled: this.defaultForm.get('diaryEnabled').value,
        genericDiaryEnabled: this.defaultForm.get('genericDiaryEnabled').value,
        imsSupplier: this.defaultForm.get('imsSupplier').value,
        imsLegacyTrial: this.defaultForm.get('imsLegacyTrial').value,
        sharepointLink: this.defaultForm.get('sharepointLink').value,
        travelAdditionalInfoEnabled: this.defaultForm.get('travelAdditionalInfoEnabled').value,
        testDataOnly: this.defaultForm.get('testDataOnly').value,
        faqsEnabled: this.defaultForm.get('faqsEnabled').value,
        selfServeNotificationsEnabled: this.defaultForm.get('selfServeNotificationsEnabled').value,
        receiptLeadExpenseEnabled: this.defaultForm.get('receiptLeadExpenseEnabled').value,
        dailySpendExpensesEnabled: this.defaultForm.get('dailySpendExpensesEnabled').value,
        dailySpendExpenseAppEnabled: this.defaultForm.get('dailySpendExpenseAppEnabled').value,
        virtualCallsEnabled: this.defaultForm.get('virtualCallsEnabled').value,
        recordingAskForConsent: this.defaultForm.get('recordingAskForConsent').value
      };

      this.trialService.updateTrial(this.trial.id, updateTrial).subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Trial Updated Successfully');
          void this.router.navigate(['/trial', this.trial.id]).then();
          this.defaultForm.patchValue({isBusy: false});
        },
        error: error => {
          LogHelper.log(error);
          this.defaultForm.patchValue({isBusy: false});
          const message = error.error ? error.error.title : 'Sorry, there was a problem';
          this.alertService.showWarningAlert(message);
        }
      });
    }
  }

  onTherapeuticAreaChanged(therapeuticArea: string) {
    this.defaultForm.patchValue({ therapeuticArea: therapeuticArea });
  }

  onBaseCurrencySelected(baseCurrency: string) {
    this.defaultForm.patchValue({ baseCurrency: baseCurrency });
  }

  onBrandChanged(brandId: string) {
    this.defaultForm.patchValue({ brandId: brandId });
    this.currentBrand = this.brands.find(b => b.id === brandId);
  }

  onConfirmBrandChange() {
    this.brandChangeConfirmed = true;
    this.brandChangeModal.hide();
    this.onFormSubmit();
  }

  onCancelBrandChange() {
    this.brandChangeModal.hide();
  }

  onConfirmApiDisable() {
    this.apiDisableConfirmed = true;
    this.apiDisableModal.hide();
    this.onFormSubmit();
  }

  /**
   * Cancelling puts the tick back, rather than leaving the box unticked with the consumer picker hidden -
   * the operator said no, so the form must not sit in the state they declined. Re-ticking also restores the
   * consumer selection via the apiEnabled subscription. AB#34863.
   */
  onCancelApiDisable() {
    this.apiDisableModal.hide();
    this.defaultForm.patchValue({ apiEnabled: true });
    this.defaultForm.patchValue({ apiConsumerIds: this.trial?.apiConsumerIds ?? [] });
  }

  onConfirmedRemoveLanguage() {
    let index = -1;
    for (let i = 0; i < this.forms.length; i++) {
      if (this.forms[i].get('culture').value === this.currentCulture) {
        index = i;
      }
    }

    if (index >= 0) {
      let cultureIndex = -1;
      for (let i = 0; i < this.culturesInUse.length; i++) {
        if (this.currentCulture === this.culturesInUse[i].value) {
          cultureIndex = i;
        }
      }

      this.forms.splice(index, 1);
      this.culturesInUse.splice(cultureIndex, 1);
      this.currentCulture = 'en';
      this.languageSelectForm.patchValue({ culture: 'en' });
      this.languageSelect.setValue('en');
    }

    this.removeLanguageModal.hide();
  }

  onAddLanguage(culture: { value: string, text: string }) {
    if (!this.isCultureInUse(culture.value)) {
      this.culturesInUse.push(culture);
      let form = new UntypedFormGroup({
        culture: new UntypedFormControl(culture.value),
        name: new UntypedFormControl(''),
        description: new UntypedFormControl('', Validators.maxLength(1000)),
        expensePolicy: new UntypedFormControl('', Validators.maxLength(5000)),
      });
      this.forms.push(form);
      this.currentCulture = culture.value;
      this.languageSelectForm.patchValue({ culture: culture.value });
      this.languageSelect.setValue(culture.value);
      this.addLanguageModal.hide();
    }
  }

  listenToTrialNameValues() {
    this.defaultForm.get('client').valueChanges.subscribe((value) => {
      this.setTrialNameValue();
    });

    this.defaultForm.get('sponsor').valueChanges.subscribe((value) => {
      this.setTrialNameValue();
    });

    this.defaultForm.get('nickname').valueChanges.subscribe((value) => {
      this.setTrialNameValue();
    });

    this.defaultForm.get('opportunityNumber').valueChanges.subscribe((value) => {
      this.setTrialNameValue();
    });
  }

  setTrialNameValue() {
    const client = this.defaultForm.get('client').value;
    const sponsor = this.defaultForm.get('sponsor').value;
    const nickname = this.defaultForm.get('nickname').value;
    const opportunityNumber = this.defaultForm.get('opportunityNumber').value;

    const value = `${client}${client && (sponsor) ? ' / ' : ''}${sponsor}${nickname && (client || sponsor) ? ' / ' : ''}${nickname}${opportunityNumber && (client || sponsor || nickname) ? ' - ' : ''}${opportunityNumber}`;

    this.defaultForm.controls['name'].setValue(value);
  }

  removeCountryFromTrial(countryCode: string): void {
    let index = this.defaultForm.get('countries').value.indexOf(countryCode);

    if (index >= 0) {
      let countries = this.defaultForm.get('countries').value;
      countries.splice(index, 1);
      this.defaultForm.patchValue({ countries: countries });
    }
  }
}
